import { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { isMobile, isTablet } from 'react-device-detect';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useSnackBar } from '../../../../contexts/snackbar';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import { CustomerDataImportDraft, DataImportResult } from '../../../../interfaces/common/data-importing';
import { CustomerType } from '../../../../interfaces/common/customer';
import { IconWithText } from '../../../common/styles/components';
import { Download, Visibility } from '@mui/icons-material';
import PreviewCustomers from './preview';

const allowedExtensions = ['.csv', '.xls', '.xlsx'];

interface MerchantBulkCreateCustomerProps {onSuccess: ()=>void}
function MerchantBulkCreateCustomers({onSuccess}: MerchantBulkCreateCustomerProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [importResult, setImportResult] = useState<DataImportResult<CustomerDataImportDraft>>()
  const [showPreview, setShowPreview] = useState(false)

  const { showMessage } = useSnackBar();
  const { config } = useContext(AuthContext);

  const reset = () => {
    setFile(undefined);
    setResponse(undefined);
    setMessage('');
    setImportResult(undefined)
  };

  const handleClose = () => {
    setDialogOpen(false);
    reset();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;
    setFile(uploadedFile);
  };

  useEffect(() => {
    if (!file) return;

    const upload = async () => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      reset();

      try {
        const response = await axios.post<DataImportResult<CustomerDataImportDraft>>(
          `${API_ENDPOINT}/api/v2/customers/import/`,
          formData,
          {
            ...config,
            headers: {
              ...config.headers,
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        setImportResult(response.data)
        setResponse(response);
      } catch (e) {
        if (isAxiosError(e)) {
          // Try to parse blob error as JSON
          const response = e.response;

          let errorMessage = 'Failed to parse customers data';
          if (
            response?.data instanceof Blob &&
            response.data.type === 'application/json'
          ) {
            try {
              const text = await response.data.text(); // Read the blob
              const json = JSON.parse(text); // Parse to JSON
              errorMessage = json.error || json.message || errorMessage;

              // Optionally, store json for rendering detailed errors
              setResponse({ ...response, data: json });
            } catch {
              // Parsing failed, fallback to default error message
            }
          } else {
            errorMessage = response?.statusText || errorMessage;
          }

          setMessage(errorMessage);
          showMessage(errorMessage, 'error');
        } else {
          setMessage('Unexpected error occurred while parsing data');
          showMessage('Unexpected error', 'error');
        }

        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };
    upload();
  }, [file]);

  const downloadFailedRecords = async () => {
    if (!importResult) return;

    try {
      setIsLoading(true);

      // Call your API to get the file (replace with your actual endpoint)
      const response = await axios.get(
        `${API_ENDPOINT}/v2/customers/import/failed-records/?file_id=${encodeURIComponent(importResult.failed_records_key)}`,
        {
          responseType: 'blob',  // Important to get file blob!
          ...config,
        }
      );

      // Create a URL for the blob and trigger the download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from the file_path or use a default
      const fileName = 'failed_records.xlsx';
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

    } catch (error) {
      showMessage('Failed to download failed records file', 'error');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div>
      <Button
        size={(isMobile && !isTablet) ? 'small' : 'medium'}
        variant="outlined"
        onClick={() => setDialogOpen(true)}
      >
        Import Excel
      </Button>

      <CustomDialog fullWidth open={dialogOpen} onClose={handleClose}>
        <DialogContent>
          <input
            ref={uploadInputRef}
            type="file"
            accept={allowedExtensions.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div>
            {message !== '' && (
              <>
                <Alert
                  severity={
                    response && response.status >= 200 && response.status < 300
                      ? 'success'
                      : 'error'
                  }
                >
                  <>
                    {message}
                    {response && response.status >= 300 && (
                      <ErrorsFlattenner rawErrors={response.data} />
                    )}
                  </>
                </Alert>
              </>
            )}

            {importResult && <>
              {importResult.success_count > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  ✅ Successfully parsed <strong>{importResult.success_count}</strong>{' '}
                  records.

                  <span
                      style={{cursor: "pointer", textDecoration: "underline"}}
                      onClick={()=>setShowPreview(true)}
                    ><IconWithText> <b>Preview parsed records.</b> <Visibility /></IconWithText>
                    </span>
                </Alert>
              )}
              {importResult.fail_count > 0 && (
                <div>
                  <br />
                  <Alert severity="warning">
                    Failed to parse <strong>{importResult.fail_count }</strong> records.  <span
                      style={{cursor: "pointer", textDecoration: "underline"}}
                      onClick={downloadFailedRecords}
                    ><IconWithText> <b>Click to download.</b> <Download /></IconWithText>
                    </span>
                    
  
                  </Alert>
                </div>
              )}
            </>
          }
            <br />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 5 }}>
            <a href="/static/sample/sample-customer-import.xlsx" download>
              <Button
                fullWidth
                variant="outlined"
                size={(isMobile && !isTablet) ? 'small' : 'medium'}
              >
                Get Template
              </Button>
            </a>
            <Button
              size={(isMobile && !isTablet) ? 'small' : 'medium'}
              onClick={() => {
                setFile(undefined)
                uploadInputRef.current?.click()
                }
              }
              variant="contained"
              disabled={isLoading}
            >
              {file ? 'Change File' : 'Choose File'}{' '}
              {isLoading && <CircularProgress sx={{ ml: 2 }} size={20} />}
            </Button>
          </div>
        </DialogContent>

        <DialogActions>
          <Button size={(isMobile && !isTablet) ? 'small' : 'medium'} onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </CustomDialog>

      {
        showPreview && importResult && <PreviewCustomers data={importResult} onClose={()=>setShowPreview(false)} onSave={()=>{onSuccess(); handleClose();}} />
      }
    </div>
  );
}

export default MerchantBulkCreateCustomers;
